/**
 * iter312 — Session backfill import sheet.
 *
 * Five stages inside one bottom-sheet, because this flow moves money:
 *   1. upload   → .xlsx/.csv (template download lives here)
 *   2. rows     → parsed sessions, errors/warnings per row
 *   3. settings → fee per player, guest fee, charge/attendance/notify toggles
 *   4. impact   → per-member "before → after and why", the approval gate
 *   5. done     → result + a one-tap Undo this import
 */
import { useState, useRef } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, Loader2, Download,
  ArrowRight, Undo2, Users, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const money = (n) => `\u20b9${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

export default function SessionImportSheet({ open, onOpenChange, groupId, groupName, onImported }) {
  const [stage, setStage] = useState('upload');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const fileRef = useRef(null);

  const [opts, setOpts] = useState({
    default_fee: '', guest_fee: '', charge_fees: true, record_attendance: true, notify: false,
  });

  const reset = () => { setStage('upload'); setPreview(null); setResult(null); };
  const close = () => { if (busy) return; reset(); onOpenChange(false); };

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/groups/${groupId}/session-import/template`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(groupName || 'group').replace(/[^A-Za-z0-9_-]/g, '_')}_session_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not download template');
    } finally {
      setDownloading(false);
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post(`/groups/${groupId}/session-import/preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setOpts((o) => ({ ...o, default_fee: res.data.default_fee ? String(res.data.default_fee) : '' }));
      setStage('rows');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read that file');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Re-price the impact locally so the approval screen reflects the fee the
  // admin just typed, without another round trip.
  const fee = Number(opts.default_fee) || 0;
  const guestFee = opts.guest_fee === '' ? fee : Number(opts.guest_fee) || 0;
  const validRows = (preview?.rows || []).filter((r) => r.errors.length === 0);
  const impact = (() => {
    const map = new Map();
    let guestHeads = 0;
    validRows.forEach((r) => {
      const rowFee = r.fee != null ? Number(r.fee) : fee;
      r.attendee_ids.forEach((uid) => {
        const cur = map.get(uid) || { user_id: uid, sessions: 0, charge: 0 };
        cur.sessions += 1;
        cur.charge += rowFee;
        map.set(uid, cur);
      });
      guestHeads += (r.guests || []).length;
    });
    const names = new Map((preview?.impact || []).map((i) => [i.user_id, i]));
    const rows = [...map.values()].map((m) => {
      const base = names.get(m.user_id) || {};
      const before = Number(base.before || 0);
      return {
        ...m,
        name: base.name || 'Member',
        before,
        after: Math.round((before - m.charge) * 100) / 100,
      };
    }).sort((a, b) => b.charge - a.charge);
    const total = rows.reduce((s, r) => s + r.charge, 0);
    return { rows, total, guestHeads, guestDues: guestHeads * guestFee };
  })();

  const confirm = async () => {
    if (!validRows.length) return toast.error('No importable rows');
    if (opts.charge_fees && fee <= 0 && validRows.every((r) => r.fee == null)) {
      return toast.error('Set a fee per player, or turn off "Charge session fees"');
    }
    setBusy(true);
    try {
      const res = await api.post(`/groups/${groupId}/session-import/confirm`, {
        rows: validRows.map((r) => ({
          date: r.date, start_time: r.start_time, title: r.title, venue: r.venue,
          fee: r.fee != null ? Number(r.fee) : null,
          attendee_ids: r.attendee_ids,
          guests: (r.guests || []).map((g) => ({ name: g.name, phone: g.phone })),
          venue_payer_id: r.venue_payer_id, notes: r.notes,
        })),
        default_fee: fee,
        guest_fee: opts.guest_fee === '' ? null : guestFee,
        charge_fees: opts.charge_fees,
        record_attendance: opts.record_attendance,
        notify: opts.notify,
      });
      setResult(res.data);
      setStage('done');
      onImported?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    if (!result?.batch_id) return;
    setUndoing(true);
    try {
      const res = await api.post(`/groups/${groupId}/session-import/${result.batch_id}/undo`);
      toast.success(res.data.message);
      onImported?.(res.data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not undo');
    } finally {
      setUndoing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl border-t-2 px-0 pt-4 pb-10 max-h-[92vh] overflow-y-auto"
        data-testid="session-import-sheet"
      >
        <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-muted-foreground/25" />
        <SheetHeader className="px-5 text-left">
          <SheetTitle className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Import past sessions
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">
            {groupName} — backfill attendance and fees from a spreadsheet
          </SheetDescription>
        </SheetHeader>

        {/* 1. UPLOAD */}
        {stage === 'upload' && (
          <div className="px-5 mt-5 space-y-4" data-testid="session-import-stage-upload">
            <Card className="p-6 border-2 border-dashed rounded-2xl text-center">
              <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-semibold mb-1">Choose your sheet</p>
              <p className="text-xs text-muted-foreground mb-4">.xlsx or .csv — up to 200 sessions</p>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile}
                className="hidden" data-testid="session-import-file-input" />
              <Button onClick={() => fileRef.current?.click()} disabled={busy}
                className="rounded-full" data-testid="session-import-choose-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Choose file
              </Button>
            </Card>

            <Card className="p-4 border-2 rounded-2xl space-y-2">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Columns</p>
              <ul className="text-xs space-y-1 text-muted-foreground leading-snug">
                <li>• <strong>Date</strong> — any common format</li>
                <li>• <strong>Attendees</strong> — comma-separated mobiles, or <code>ALL</code></li>
                <li>• <strong>Guests</strong> — <code>Name:9810011122</code>, billed as cash dues</li>
                <li>• <strong>Fee Per Player</strong> — optional per-row override</li>
                <li>• <em>Time, Title, Venue, Venue Payer, Notes</em> — optional</li>
              </ul>
              <Button variant="outline" size="sm" onClick={downloadTemplate} disabled={downloading}
                data-testid="session-import-download-template-btn"
                className="rounded-full w-full mt-3 border-2 border-dashed border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10 text-xs">
                {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                Download template
              </Button>
            </Card>
          </div>
        )}

        {/* 2. ROWS */}
        {stage === 'rows' && preview && (
          <div className="px-5 mt-5 space-y-3" data-testid="session-import-stage-rows">
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Ready', value: preview.summary.valid_rows, tone: 'text-emerald-600' },
                { label: 'Skipped', value: preview.summary.error_rows, tone: 'text-rose-600' },
                { label: 'Guests', value: preview.summary.guest_heads, tone: 'text-amber-600' },
              ].map((s) => (
                <Card key={s.label} className="p-2.5 border-2 rounded-xl text-center">
                  <p className={`font-display text-lg font-bold tabular-nums ${s.tone}`}>{s.value}</p>
                  <p className="text-xs text-muted-foreground font-semibold">{s.label}</p>
                </Card>
              ))}
            </div>

            <div className="space-y-2 max-h-[46vh] overflow-y-auto pr-0.5">
              {preview.rows.map((r) => (
                <Card key={r.sheet_row} data-testid={`session-import-row-${r.sheet_row}`}
                  className={`p-3 border-2 rounded-xl ${r.errors.length ? 'border-rose-500/40 bg-rose-500/5' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold truncate">{r.title || `Session — ${r.date || '?'}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.date || 'no date'} · {r.start_time}
                        {r.venue ? ` · ${r.venue}` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-bold tabular-nums">{r.attendee_ids.length} played</p>
                      {!!(r.guests || []).length && (
                        <p className="text-xs text-amber-600 font-semibold">+{r.guests.length} guest</p>
                      )}
                    </div>
                  </div>
                  {!!r.attendee_names?.length && (
                    <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
                      {r.attendee_names.join(', ')}
                    </p>
                  )}
                  {r.errors.map((e) => (
                    <p key={e} className="text-xs font-semibold text-rose-600 mt-1 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{e}
                    </p>
                  ))}
                  {r.warnings.map((w) => (
                    <p key={w} className="text-xs text-amber-600 mt-1">{w}</p>
                  ))}
                </Card>
              ))}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full flex-1 border-2" onClick={reset}
                data-testid="session-import-back-upload">Start over</Button>
              <Button className="rounded-full flex-1" disabled={!preview.summary.valid_rows}
                onClick={() => setStage('settings')} data-testid="session-import-to-settings">
                Next <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* 3. SETTINGS */}
        {stage === 'settings' && (
          <div className="px-5 mt-5 space-y-3" data-testid="session-import-stage-settings">
            <Card className="p-4 border-2 rounded-2xl space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Fee / player</Label>
                  <Input type="number" min="0" value={opts.default_fee} placeholder="150"
                    onChange={(e) => setOpts({ ...opts, default_fee: e.target.value })}
                    className="h-11 rounded-xl border-2 mt-1" data-testid="session-import-fee-input" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Guest / head</Label>
                  <Input type="number" min="0" value={opts.guest_fee} placeholder={fee ? String(fee) : 'same'}
                    onChange={(e) => setOpts({ ...opts, guest_fee: e.target.value })}
                    className="h-11 rounded-xl border-2 mt-1" data-testid="session-import-guest-fee-input" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                A <strong>Fee Per Player</strong> value in the sheet overrides this per row. Guest dues stay
                pending until you record the cash.
              </p>
            </Card>

            {[
              { k: 'charge_fees', title: 'Charge session fees', sub: 'Debits each attendee and credits the group fund', testid: 'charge-fees' },
              { k: 'record_attendance', title: 'Count in attendance stats', sub: 'Adds these to attendance, streaks and reconcile', testid: 'record-attendance' },
              { k: 'notify', title: 'Notify attendees', sub: 'Off is best for a backfill — avoids a flood of alerts', testid: 'notify' },
            ].map((t) => (
              <Card key={t.k} className="p-3.5 border-2 rounded-2xl flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold">{t.title}</p>
                  <p className="text-xs text-muted-foreground">{t.sub}</p>
                </div>
                <Switch checked={opts[t.k]} onCheckedChange={(v) => setOpts({ ...opts, [t.k]: v })}
                  data-testid={`session-import-toggle-${t.testid}`} />
              </Card>
            ))}

            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full flex-1 border-2" onClick={() => setStage('rows')}
                data-testid="session-import-back-rows">Back</Button>
              <Button className="rounded-full flex-1" onClick={() => setStage('impact')}
                data-testid="session-import-to-impact">
                See balance impact <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        )}

        {/* 4. IMPACT — the approval gate */}
        {stage === 'impact' && (
          <div className="px-5 mt-5 space-y-3" data-testid="session-import-stage-impact">
            <Card className="p-4 border-2 rounded-2xl">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                What this import does
              </p>
              <div className="space-y-1.5 text-sm">
                <p className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />Sessions added
                  </span>
                  <span className="font-bold tabular-nums">{validRows.length}</span>
                </p>
                <p className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Wallet className="w-3.5 h-3.5" />
                    {opts.charge_fees ? 'Charged to members' : 'Fees charged'}
                  </span>
                  <span className="font-bold tabular-nums">
                    {opts.charge_fees ? money(impact.total) : 'none'}
                  </span>
                </p>
                {!!impact.guestHeads && (
                  <p className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Guest dues (cash, pending)</span>
                    <span className="font-bold tabular-nums text-amber-600">{money(impact.guestDues)}</span>
                  </p>
                )}
                <p className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Group fund after</span>
                  <span className="font-bold tabular-nums">
                    {money((preview?.group?.fund_balance || 0) + (opts.charge_fees ? impact.total : 0))}
                  </span>
                </p>
              </div>
            </Card>

            {opts.charge_fees && (
              <>
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Every balance that changes
                </p>
                <div className="space-y-1.5 max-h-[38vh] overflow-y-auto pr-0.5">
                  {impact.rows.map((m) => (
                    <Card key={m.user_id} className="p-3 border-2 rounded-xl" data-testid={`session-import-impact-${m.user_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-bold truncate">{m.name}</p>
                        <p className="text-sm font-bold tabular-nums text-rose-600">−{money(m.charge)}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {m.sessions} session{m.sessions > 1 ? 's' : ''} ·{' '}
                        <span className="tabular-nums">{money(m.before)}</span>
                        <ArrowRight className="w-3 h-3 inline mx-1" />
                        <span className={`tabular-nums font-bold ${m.after < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {money(m.after)}
                        </span>
                      </p>
                    </Card>
                  ))}
                </div>
              </>
            )}

            <Card className="p-3 border-2 border-amber-500/40 bg-amber-500/5 rounded-xl">
              <p className="text-xs font-semibold flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
                Check these numbers. You can undo the whole import in one tap right after it runs.
              </p>
            </Card>

            <div className="flex gap-2">
              <Button variant="outline" className="rounded-full flex-1 border-2" onClick={() => setStage('settings')}
                disabled={busy} data-testid="session-import-back-settings">Back</Button>
              <Button className="rounded-full flex-1" onClick={confirm} disabled={busy}
                data-testid="session-import-confirm-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Looks right, import
              </Button>
            </div>
          </div>
        )}

        {/* 5. DONE */}
        {stage === 'done' && result && (
          <div className="px-5 mt-5 space-y-3" data-testid="session-import-stage-done">
            <Card className="p-5 border-2 rounded-2xl text-center">
              <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
              <p className="text-sm font-bold">{result.sessions_created} session(s) imported</p>
              <p className="text-xs text-muted-foreground mt-1">{result.message}</p>
            </Card>
            <Button variant="outline" onClick={undo} disabled={undoing}
              className="rounded-full w-full border-2 border-rose-500/40 text-rose-600 hover:bg-rose-500/10"
              data-testid="session-import-undo-btn">
              {undoing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Undo2 className="w-4 h-4 mr-2" />}
              Undo this import
            </Button>
            <Button className="rounded-full w-full" onClick={close} data-testid="session-import-done-btn">
              Done
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
